import React, { useEffect, useRef, useState } from "react";
import { SketchPicker, ColorResult } from "react-color";

/* region Imports - Libraries */
import { useForm } from "react-hook-form";
import { useToast } from "@npm-telluria-tecnologia/telluria-ui";
/* endregion Imports - Libraries */
/* region Imports - GraphQL */
import { useLazyQuery, useMutation } from "@apollo/client";
import { paramAllWithPagination, TParamAllWithPaginationQueries } from "@graphql/groups/param/query.graphql";
import {
  EWhereClausesOperators,
  ParamMutationsUpdateArgs,
  ParamQueriesGetAllWithPaginationArgs
} from "@graphql/helpers/types.graphql";
import { paramUpdateMutation, TParamUpdateMutations } from "@graphql/groups/param/mutation.graphql";
/* endregion Imports - GraphQL */
/* region Imports - Atoms */
import Loading from "@atoms/Loading";
/* endregion Imports - Atoms */
/* region Imports - Constants */
import { EParamsId } from "src/constants/enum/EParamsId";
/* endregion Imports - Constants */
/* region Imports - Language */
import { GlobalMessages, SettingMessages } from "@languages/interfaces";
import useTranslation from "@languages/useTranslation";
/* endregion Imports - Language */
/* region Imports - Styles */
import Styled from "./styles";
/* endregion Imports - Styles */

// region Interfaces
interface IConfigurableColors {
  primary: string;
  secondary: string;
  tertiary: string;
  disabled: string;
  loginBackground: string;
}
// endregion Interfaces

const FormEnvColors: React.FC = () => {

  /* region States */
  const [displayPrimaryColorPicker, setDisplayPrimaryColorPicker] = useState(false);
  const [displaySecondaryColorPicker, setDisplaySecondaryColorPicker] = useState(false);
  const [displayTertiaryColorPicker, setDisplayTertiaryColorPicker] = useState(false);
  const [displayDisableColorPicker, setDisplayDisableColorPicker] = useState(false);
  const [displayLoginBackgroundColorPicker, setDisplayLoginBackgroundColorPicker] = useState(false);

  const [configurableColors, setConfigurableColors] = useState<IConfigurableColors>({
    primary: "#000000",
    secondary: "#000000",
    tertiary: "#000000",
    disabled: "#000000",
    loginBackground: "#56C2FF"
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /* endregion States */
  /* region Hooks */
  const { addToast } = useToast();
  const configurableColorsRef = useRef<IConfigurableColors>({} as IConfigurableColors);
  const { t } = useTranslation();
  const { handleSubmit } = useForm<IConfigurableColors>();
  /* endregion Hooks */
  /* region GraphQL */
  const [getParams] = useLazyQuery<
    TParamAllWithPaginationQueries,
    ParamQueriesGetAllWithPaginationArgs
  >(paramAllWithPagination(["id", "name", "key"]), {
    fetchPolicy: "network-only"
  });

  const [updateParamMutation] = useMutation<
    TParamUpdateMutations,
    ParamMutationsUpdateArgs
  >(
    paramUpdateMutation(["id", "name", "key"])
  );

  /* endregion GraphQL */
  /* region Functions */

  /*
    * Update, if necessary, the color params in database
   */
  const FsaveColors = async () => {

    try {

      setIsLoading(true);

      const hasUpdated = Object.keys(configurableColorsRef.current).some(
        (key) => configurableColorsRef.current[key] !== configurableColors[key]
      );

      if (!hasUpdated) {
        addToast(
          {
            title: t(GlobalMessages.warningText),
            description: t(SettingMessages.settingColorsNoChange),
            type: "info"
          }
        );

        return;
      }

      // Get the theme colors (without loginBackground)
      const { loginBackground, ...themeColors } = configurableColors;

      const themeColorsJson = JSON.stringify(themeColors);

      const [
        updateColorThemeResponse,
        updateLoginBackgroundColor
      ] = await Promise.all([
        updateParamMutation({
          variables: {
            param: {
              id: EParamsId.THEME,
              key: themeColorsJson
            }
          }
        }),
        updateParamMutation({
          variables: {
            param: {
              id: EParamsId.LOGIN_COLOR,
              key: configurableColors.loginBackground
            }
          }
        })
      ]);

      if (
        updateColorThemeResponse.data?.param.update.status === "SUCCESS"
        && updateLoginBackgroundColor.data?.param.update.status === "SUCCESS"
      ) {
        configurableColorsRef.current = {
          ...configurableColors
        };

        localStorage.setItem("@Topcon:themeColors", themeColorsJson);

        window.location.reload();

      }

    } catch (error) {
      addToast(
        {
          title: `${t(GlobalMessages.errorText)}!`,
          description: error.message,
          type: "error"
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * Get the environment colors from database
   */
  const FgetColors = async () => {

    setIsLoading(true);

    try {
      const { data } = await getParams({
        variables: {
          where: [
            {
              property: "id",
              operator: EWhereClausesOperators.IN,
              value: `${EParamsId.THEME} | ${EParamsId.LOGIN_COLOR}`
            }
          ]
        }
      });

      if (data?.param.getAllWithPagination.status === "SUCCESS") {

        const result = data?.param.getAllWithPagination.result;

        const parsedThemeColorResult = JSON.parse(
          result?.find((item) => item?.id === EParamsId.THEME)?.key ?? "{}"
        );

        const loginBackgroundColor = result?.find((item) => item?.id === EParamsId.LOGIN_COLOR)?.key;

        const configurableColors: IConfigurableColors = {
          primary: parsedThemeColorResult?.primary,
          secondary: parsedThemeColorResult?.secondary,
          tertiary: parsedThemeColorResult?.tertiary,
          disabled: parsedThemeColorResult?.disabled,
          loginBackground: loginBackgroundColor ?? "#56C2FF"
        };

        setConfigurableColors(configurableColors);

        configurableColorsRef.current = configurableColors;
      } else {
        addToast({
          title: t(GlobalMessages.errorText),
          description: data?.param.getAllWithPagination.message,
          type: "error"
        });
      }
    } catch (error) {
      addToast(
        {
          title: `${t(GlobalMessages.errorText)}!`,
          description: error.message,
          type: "error"
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  /* endregion Functions */
  /* region useEffects */

  useEffect(() => {

    FgetColors().then();
  }, [getParams]);
  /* endregion useEffects */

  return (
    <Styled.ParamsContainer>
      <Loading loading={isLoading} />
      {!isLoading && (
        <form onSubmit={handleSubmit(FsaveColors)}>
          <Styled.ParamItem>
            <Styled.ParamItemText>{t(SettingMessages.settingColorsPrimary)}</Styled.ParamItemText>
            <div>
              <Styled.Swatch onClick={() => setDisplayPrimaryColorPicker(true)}>
                <Styled.ColorBox color={configurableColors.primary} />
              </Styled.Swatch>
              {displayPrimaryColorPicker && (
                <Styled.ColorPickerPopover>
                  <Styled.ColorPickerCover onClick={() => setDisplayPrimaryColorPicker(false)} />
                  <SketchPicker
                    color={configurableColors.primary}
                    onChange={(colorResult: ColorResult) => setConfigurableColors((prevState) => ({
                      ...prevState,
                      primary: colorResult.hex
                    }))}
                  />
                </Styled.ColorPickerPopover>
              )}
            </div>
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>{t(SettingMessages.settingColorsSecondary)}</Styled.ParamItemText>
            <div>
              <Styled.Swatch onClick={() => setDisplaySecondaryColorPicker(true)}>
                <Styled.ColorBox color={configurableColors.secondary} />
              </Styled.Swatch>
              {displaySecondaryColorPicker && (
                <Styled.ColorPickerPopover>
                  <Styled.ColorPickerCover onClick={() => setDisplaySecondaryColorPicker(false)} />
                  <SketchPicker
                    color={configurableColors.secondary}
                    onChange={(colorResult: ColorResult) => setConfigurableColors((prevState) => ({
                      ...prevState,
                      secondary: colorResult.hex
                    }))}
                  />
                </Styled.ColorPickerPopover>
              )}
            </div>
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>{t(SettingMessages.settingColorsTertiary)}</Styled.ParamItemText>
            <div>
              <Styled.Swatch onClick={() => setDisplayTertiaryColorPicker(true)}>
                <Styled.ColorBox
                  color={configurableColors.tertiary}
                />
              </Styled.Swatch>
              {displayTertiaryColorPicker && (
                <Styled.ColorPickerPopover>
                  <Styled.ColorPickerCover onClick={() => setDisplayTertiaryColorPicker(false)} />
                  <SketchPicker
                    color={configurableColors.tertiary}
                    onChange={(colorResult: ColorResult) => setConfigurableColors((prevState) => ({
                      ...prevState,
                      tertiary: colorResult.hex
                    }))}
                  />
                </Styled.ColorPickerPopover>
              )}
            </div>
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>{t(SettingMessages.settingsColorsDisable)}</Styled.ParamItemText>
            <div>
              <Styled.Swatch onClick={() => setDisplayDisableColorPicker(true)}>
                <Styled.ColorBox
                  color={configurableColors.disabled}
                />
              </Styled.Swatch>
              {displayDisableColorPicker && (
                <Styled.ColorPickerPopover>
                  <Styled.ColorPickerCover onClick={() => setDisplayDisableColorPicker(false)} />
                  <SketchPicker
                    color={configurableColors.disabled}
                    onChange={(colorResult: ColorResult) => setConfigurableColors((prevState) => ({
                      ...prevState,
                      disabled: colorResult.hex
                    }))}
                  />
                </Styled.ColorPickerPopover>
              )}
            </div>
          </Styled.ParamItem>
          <Styled.ParamItem>
            <Styled.ParamItemText>{t(SettingMessages.settingsColorsLoginBackground)}</Styled.ParamItemText>
            <div>
              <Styled.Swatch onClick={() => setDisplayLoginBackgroundColorPicker(true)}>
                <Styled.ColorBox
                  color={configurableColors.loginBackground}
                />
              </Styled.Swatch>
              {displayLoginBackgroundColorPicker && (
                <Styled.ColorPickerPopover>
                  <Styled.ColorPickerCover onClick={() => setDisplayLoginBackgroundColorPicker(false)} />
                  <SketchPicker
                    color={configurableColors.loginBackground}
                    onChange={(colorResult: ColorResult) => setConfigurableColors((prevState) => ({
                      ...prevState,
                      loginBackground: colorResult.hex
                    }))}
                  />
                </Styled.ColorPickerPopover>
              )}
            </div>
          </Styled.ParamItem>
          <Styled.SubmitContainer>
            <Styled.SubmitButton className="confirmButton" type="submit" disabled={isLoading}>
              {t(GlobalMessages.buttonSaveChanges)}
            </Styled.SubmitButton>
            <Styled.ReloadSubmitInfoSpan>
              **{t(SettingMessages.settingNotificationReload)}
            </Styled.ReloadSubmitInfoSpan>
          </Styled.SubmitContainer>
        </form>
      )}
    </Styled.ParamsContainer>
  );
};

export default FormEnvColors;
